// Ahri orb VFX: glowing ball travels from caster to the end of the line,
// then returns back. Fires popup callbacks when orb reaches each hit position.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const OUT_DURATION = 280;
const BACK_DURATION = 280;
const TOTAL = OUT_DURATION + BACK_DURATION;

export type OrbHit = { x: number; y: number; amount: number; color: string; pass: "out" | "back" };

export class OrbVFX implements Animation {
  private elapsed = 0;
  private waypointsBuilt = false;
  private waypoints: { tMs: number; hit: OrbHit; fired: boolean }[] = [];

  constructor(
    private from: Point,
    private to: Point,
    private color: string = "#00b4ff",
    private pendingHits: OrbHit[] = [],
    private onHitFired?: (hit: OrbHit) => void,
  ) {}

  update(dt: number): void {
    if (!this.waypointsBuilt) {
      this.buildWaypoints();
      this.waypointsBuilt = true;
    }
    this.elapsed = Math.min(this.elapsed + dt, TOTAL);
    for (const wp of this.waypoints) {
      if (!wp.fired && this.elapsed >= wp.tMs) {
        wp.fired = true;
        this.onHitFired?.(wp.hit);
      }
    }
  }

  private buildWaypoints(): void {
    const dx = this.to.x - this.from.x;
    const dy = this.to.y - this.from.y;
    const lenSq = dx * dx + dy * dy;

    for (const hit of this.pendingHits) {
      const px = hit.x - this.from.x;
      const py = hit.y - this.from.y;
      const ratio = lenSq > 0 ? Math.max(0.05, Math.min(0.95, (px * dx + py * dy) / lenSq)) : 0.5;
      const tMs =
        hit.pass === "out"
          ? ratio * OUT_DURATION
          : OUT_DURATION + (1 - ratio) * BACK_DURATION;
      this.waypoints.push({ tMs, hit, fired: false });
    }
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const going = this.elapsed < OUT_DURATION;
    const t = going
      ? Easing.easeOutQuad(this.elapsed / OUT_DURATION)
      : Easing.easeOutQuad((this.elapsed - OUT_DURATION) / BACK_DURATION);

    const start = going ? this.from : this.to;
    const end = going ? this.to : this.from;

    const x = start.x + (end.x - start.x) * t;
    const y = start.y + (end.y - start.y) * t;

    // Trail
    const trailLen = going ? t : 1 - t;
    const trailX = start.x + (end.x - start.x) * trailLen * 0.6;
    const trailY = start.y + (end.y - start.y) * trailLen * 0.6;
    ctx.save();
    ctx.globalAlpha = 0.45;
    ctx.strokeStyle = this.color;
    ctx.lineWidth = 5;
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 12;
    ctx.beginPath();
    ctx.moveTo(trailX, trailY);
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.restore();

    // Outer glow
    ctx.save();
    ctx.shadowColor = this.color;
    ctx.shadowBlur = 28;
    ctx.globalAlpha = 0.5;
    ctx.fillStyle = this.color;
    ctx.beginPath();
    ctx.arc(x, y, 14, 0, Math.PI * 2);
    ctx.fill();

    // Mid ring
    ctx.globalAlpha = 0.75;
    ctx.fillStyle = "#80d8ff";
    ctx.beginPath();
    ctx.arc(x, y, 9, 0, Math.PI * 2);
    ctx.fill();

    // Bright white core
    ctx.shadowBlur = 10;
    ctx.globalAlpha = 0.95;
    ctx.fillStyle = "#ffffff";
    ctx.beginPath();
    ctx.arc(x, y, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL;
  }
}
